import { Document, Packer, SectionType } from 'docx';
import { ElearningDataType } from '@/modules/types/elearning';
import { FrameDataType } from '@/modules/types/frame';
import { getHtmlExportNumberingConfig, sanitizeFileNameBase } from './common';
import { handleElearningData } from './handleElearningData';
import { handleFrameData } from './handleFrameData';

/**
 * Maps e-learning frames and metadata to a .docx binary using the `docx` package.
 */
export async function buildElearningDocx(
  frames: FrameDataType[],
  elearningData: ElearningDataType,
): Promise<Blob> {
  const elearningDocument = new Document({
    numbering: getHtmlExportNumberingConfig(),
    sections: [
      {
        // Elearning Summary
        children: [...handleElearningData(elearningData)],
      },
      {
        // Frames
        properties: {
          type: SectionType.CONTINUOUS,
        },
        children: [...handleFrameData(frames, elearningData.units)],
      },
    ],
  });

  return Packer.toBlob(elearningDocument);
}

/**
 * Triggers a browser download of a .docx blob. Does not use `docx` packing.
 */
export function downloadDocx(blob: Blob, fileNameBase: string): void {
  const base = sanitizeFileNameBase(fileNameBase);
  const fileName = base.toLowerCase().endsWith('.docx') ? base : `${base}.docx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Exports the module to a Word document: builds the document, then downloads it.
 */
export const exportToDocument = async (
  frames: FrameDataType[],
  elearningData: ElearningDataType,
): Promise<void> => {
  const blob = await buildElearningDocx(frames, elearningData);
  const fileNameBase = `${elearningData.name} Document`;
  downloadDocx(blob, fileNameBase);
};
